/**
 * The one-line pin of `GET /api/agents/{domain}/memory/hero` (#42 §5).
 *
 * The hero endpoint answers with an entry OR with an explicit reason there
 * is none. Both are rendered: "4 beliefs waiting for their first test" is
 * a true and useful sentence, and it is what stands in place of the
 * learning — never a fabricated one, and never an empty box.
 */

import { Text } from '@shopify/polaris';

import { isHeroEmpty, isMemoryEntry } from '../../types/agentMind';
import type { MemoryHeroResponse } from '../../types/agentMind';
import styles from './workspace.module.css';

export function LearningPin({
  hero,
  error,
}: {
  hero: MemoryHeroResponse | null;
  error?: string | null;
}) {
  if (error) {
    return (
      <div className={styles.learningPin} data-testid="learning-pin">
        <Text as="p" tone="subdued">
          What this agent learned most recently could not be read.
        </Text>
      </div>
    );
  }
  if (!hero) return null;

  if (!isHeroEmpty(hero) && !isMemoryEntry(hero)) {
    // Neither the entry nor the documented empty state: say so, do not
    // reach into it for a number that may not be there.
    return (
      <div className={styles.learningPin} data-testid="learning-pin">
        <Text as="p" tone="subdued">
          What this agent learned most recently could not be read.
        </Text>
      </div>
    );
  }

  if (isHeroEmpty(hero)) {
    return (
      <div className={styles.learningPin} data-testid="learning-pin">
        <Text as="p" tone="subdued">
          No tests recorded yet — {hero.beliefs_awaiting_first_test} beliefs
          waiting for their first test.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.learningPin} data-testid="learning-pin">
      <Text as="p" variant="bodySm">
        {hero.statement}
      </Text>
      <div className={styles.subtle}>
        <span className={styles.mono}>
          {hero.delta >= 0 ? '+' : ''}
          {hero.delta.toFixed(2)}
        </span>{' '}
        confidence
        {hero.sample_size !== null ? (
          <>
            {' · n='}
            <span className={styles.mono}>{hero.sample_size}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}
